import React from "react";
import { Card, H1, H3, Tag, Spinner, NonIdealState, Button } from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import { apiCallGet } from "../fns/util";
import cogoToast from "cogo-toast";
import { withRouter } from "react-router";
import { indianRupee, niceDateTime } from "../fns/util";

class PaymentReceiptPage extends React.Component {
  state = {
    isLoading: true,
    booking: null,
    payments: null,
    error: null,
  };

  componentDidMount() {
    this.loadPaymentData();
  }

  loadPaymentData = async () => {
    try {
      const sxId = this.props.match.params.sxId;
      if (!sxId) {
        this.setState({
          error: "Surgery ID not provided",
          isLoading: false,
        });
        return;
      }

      console.log("🔄 Loading payment data for surgery:", sxId);
      
      // Load booking data with payment information
      const response = await apiCallGet(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getByIdForBill/${sxId}`
      );

      console.log("📄 Payment data response:", response);

      if (response && response.success && response.data) {
        this.setState({
          booking: response.data,
          payments: response.data.payments || { events: [], totalPaid: 0, totalRefunded: 0 },
          isLoading: false,
        });
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("❌ Error loading payment data:", err);
      this.setState({
        error: "Failed to load payment data",
        isLoading: false,
      });
      cogoToast.error("Failed to load payment data");
    }
  };

  renderPaymentEvent = (event, index) => {
    return (
      <Card key={index} style={{ marginBottom: "10px", backgroundColor: "var(--bg-surface)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>
              {event.moneyReceiptNo || `Receipt #${index + 1}`}
            </H3>
            <div style={{ marginTop: "10px" }}>
              <Tag large minimal style={{ marginRight: "10px" }}>
                {event.type || "Payment"}
              </Tag>
              <Tag large minimal style={{ marginRight: "10px" }}>
                {event.mode || "Cash"}
              </Tag>
              <Tag large minimal intent="success">
                {indianRupee(event.amountPaise || event.amount * 100 || 0)}
              </Tag>
            </div>
          </div>
          <div style={{ textAlign: "right", color: "var(--text-secondary)" }}>
            <div>By: {event.doneBy || "Unknown"}</div>
            <div>{event.ts ? niceDateTime(event.ts) : "No timestamp"}</div>
          </div>
        </div>
      </Card>
    );
  };

  renderPaymentSummary = () => {
    const { payments } = this.state;
    
    return (
      <Card style={{ marginBottom: "20px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Payment Summary</H3>
        <div style={{ marginTop: "15px", display: "flex", gap: "20px" }}>
          <div>
            <Tag large minimal intent="success">
              Total Paid: {indianRupee(payments.totalPaidPaise || payments.totalPaid * 100 || 0)}
            </Tag>
          </div>
          {(payments.totalRefundedPaise > 0 || payments.totalRefunded > 0) ? (
            <div>
              <Tag large minimal intent="warning">
                Total Refunded: {indianRupee(payments.totalRefundedPaise || payments.totalRefunded * 100 || 0)}
              </Tag>
            </div>
          ) : null}
          <div>
            <Tag large minimal>
              Net Amount: {indianRupee(
                (payments.totalPaidPaise || payments.totalPaid * 100 || 0) - 
                (payments.totalRefundedPaise || payments.totalRefunded * 100 || 0)
              )}
            </Tag>
          </div>
        </div>
      </Card>
    );
  };

  renderPatientInfo = () => {
    const { booking } = this.state;
    
    return (
      <Card style={{ marginBottom: "20px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Patient Information</H3>
        <div style={{ marginTop: "15px" }}>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Patient Name: {booking.patientName || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Patient ID: {booking.patientId || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Booking Reference: {booking.bookingReference || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Doctor: {booking.doctorName || "Unknown"}</Tag>
          </div>
          <div>
            <Tag large minimal>Branch: {booking.branchName || "Unknown"}</Tag>
          </div>
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.isLoading) {
      return (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "var(--bg-primary)", 
          color: "var(--text-primary)", 
          minHeight: "100vh" 
        }}>
          <NonIdealState
            icon={<Spinner />}
            title="Loading Payment Data..."
            description="Please wait while we fetch the payment information."
          />
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "var(--bg-primary)", 
          color: "var(--text-primary)", 
          minHeight: "100vh" 
        }}>
          <NonIdealState
            icon="error"
            title="Error Loading Payment Data"
            description={this.state.error}
            action={
              <Button 
                intent="primary" 
                icon="refresh" 
                onClick={this.loadPaymentData}
              >
                Retry
              </Button>
            }
          />
        </div>
      );
    }

    const { booking, payments } = this.state;
    const paymentEvents = payments?.events || [];

    return (
      <div style={{ 
        padding: "20px", 
        backgroundColor: "var(--bg-primary)", 
        color: "var(--text-primary)", 
        minHeight: "100vh" 
      }}>
        <div style={{ marginBottom: "20px" }}>
          <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
            Payments & Money Receipts
          </H1>
          <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
            Surgery ID: {this.props.match.params.sxId}
          </p>
        </div>

        {/* Patient Information */}
        {this.renderPatientInfo()}

        {/* Payment Summary */}
        {this.renderPaymentSummary()}

        {/* Payment Events */}
        <div>
          <H3 style={{ color: "var(--text-primary)", marginBottom: "15px" }}>
            Payment History ({paymentEvents.length} transactions)
          </H3>
          
          {paymentEvents.length === 0 ? (
            <NonIdealState
              icon="credit-card"
              title="No Payments Found"
              description="No payment transactions have been recorded for this surgery."
            />
          ) : (
            <div>
              {paymentEvents.map((event, index) => this.renderPaymentEvent(event, index))}
            </div>
          )}
        </div>

        {/* Print Actions */}
        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <Button 
            intent="primary" 
            icon="print" 
            large
            onClick={() => window.print()}
            style={{ marginRight: "10px" }}
          >
            Print Receipts
          </Button>
          <Button 
            icon="arrow-left" 
            large
            onClick={() => window.history.back()}
          >
            Back to Billing
          </Button>
        </div>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(PaymentReceiptPage)));